import {ReferenceError, documentElement} from "@acng/frontend-bounty";
import {getParentElement} from "@acng/frontend-bounty/dom/query.js";
import {createWeakMap, store, get as weakGet} from "@acng/frontend-bounty/collection.js";
import {create} from "@acng/frontend-bounty/object.js";

import {createContextProvider, subscribe} from "./context-provider.js";
import {
  CTX_ALERT,
  CTX_OBSERVE,
  CTX_PROVIDE,
  CTX_REVOKE,
  CTX_SUBSCRIBE_TAGNAME,
  CTX_UNOBSERVE,
  CTX_VALUE,
} from "../minify.js";

const MODULE = "relativity/context";

/**
 * @type {WeakMap<Element, {[key: symbol]: ?import("./context-interface").Context<any>}>}
 */
const providers = createWeakMap();

/**
 * @template T
 * @returns {import("./context-interface").Context<T>}
 */
export function createContext() {
  const contextSymbol = Symbol();
  /**
   * @param {?Element} consumer
   */
  const get = (consumer) => {
    for (let p = consumer; p; p = getParentElement(p)) {
      let found = weakGet(providers, p)?.[contextSymbol];
      if (found) {
        return found;
      }
    }
    DEBUG: console.error(MODULE, `missing context`, {consumer});
    throw ReferenceError();
  };

  return {
    [CTX_PROVIDE]: (providerElement, value) => {
      providerElement ??= documentElement;
      const localContextProvider = store(providers, providerElement, () => create());
      const context = localContextProvider[contextSymbol];
      DEBUG: if (providerElement.hasAttribute('debug')) {
        console.debug(MODULE, 'provide', {element: providerElement, value, init: !!context});
      }
      if (!context) {
        localContextProvider[contextSymbol] = createContextProvider(value, contextSymbol);
      } else {
        context[CTX_PROVIDE](providerElement, value);
      }
      return value;
    },
    [CTX_REVOKE]: (providerElement) => {
      providerElement ??= documentElement;
      DEBUG: if (providerElement.hasAttribute('debug')) {
        console.debug(MODULE, 'revoke', {element: providerElement});
      }
      const provider = weakGet(providers, providerElement ?? documentElement);
      if (provider) {
        delete provider[contextSymbol];
      }
    },
    [CTX_VALUE]: (consumer) => get(consumer)[CTX_VALUE](consumer),
    [CTX_SUBSCRIBE_TAGNAME]: (selector) => subscribe(contextSymbol, selector),
    [CTX_OBSERVE]: (consumer, handler, observerSymbol) => get(consumer)[CTX_OBSERVE](consumer, handler, observerSymbol ?? contextSymbol),
    [CTX_ALERT]: (consumer, handler) => get(consumer)[CTX_ALERT](consumer, handler, contextSymbol),
    [CTX_UNOBSERVE]: (consumer) => get(consumer)[CTX_UNOBSERVE](consumer, contextSymbol),
  };
}
